import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import type {
  CreateDropChannel,
  DropChannel,
  Note,
  InputsSnapshot,
  Todo,
  TodoEvent,
  TodoPriority,
  TodoProposal,
  TodoStatus,
} from '@almanac/contracts';
import { MarkdownPrimitive } from '@almanac/catalog';
import { useApi } from '../api/useApi';
import { useMe } from '../api/MeContext';
import { useAuth } from '../auth/AuthContext';
import type { ApiClient } from '../api/client';
import { useGlobalInputs } from '../components/inputs/InputsContext';
import { ResearchStatePill, useResearchPolling } from '../components/inputs/ResearchStatePill';

type Tab = 'todos' | 'research' | 'guidance' | 'drop-channels' | 'proposals' | 'archive';
const TABS: Tab[] = ['todos', 'research', 'guidance', 'drop-channels', 'proposals', 'archive'];

function isTab(value: string | null): value is Tab {
  return value !== null && TABS.includes(value as Tab);
}

interface LinkedTarget {
  kind: 'note' | 'todo';
  id: string;
}

/** Citation hashes identify one server-authorized input, including rows outside list caps. */
function linkedInputTarget(hash: string): LinkedTarget | null {
  let anchor: string;
  try {
    anchor = decodeURIComponent(hash.slice(1));
  } catch {
    return null;
  }
  if (anchor.startsWith('todo-') && anchor.length > 5) return { kind: 'todo', id: anchor.slice(5) };
  if (anchor.startsWith('input-') && anchor.length > 6)
    return { kind: 'note', id: anchor.slice(6) };
  return null;
}

/** Resolve missing citation rows once per identity and target; ignore obsolete requests. */
function useLinkedInput(
  api: ApiClient,
  userId: string | null,
  sessionVersion: number,
  authenticated: boolean,
  target: LinkedTarget | null,
  snapshot: InputsSnapshot | null,
) {
  type Loaded = {
    api: ApiClient;
    snapshot: InputsSnapshot | null;
    userId: string | null;
    sessionVersion: number;
    id: string;
    kind: LinkedTarget['kind'];
    record: Note | Todo | null;
    error: boolean;
  };
  const [loaded, setLoaded] = useState<Loaded | null>(null);
  let present: Note | Todo | undefined;
  if (target?.kind === 'todo') present = snapshot?.todos.find((row) => row.todo_id === target.id);
  else if (target) present = snapshot?.notes.find((row) => row.note_id === target.id);
  const targetId = target?.id;
  const targetKind = target?.kind;
  const ready = snapshot !== null;
  const isPresent = present !== undefined;
  const matches =
    loaded?.api === api &&
    loaded.snapshot === snapshot &&
    loaded.userId === userId &&
    loaded.sessionVersion === sessionVersion &&
    authenticated &&
    loaded.id === targetId &&
    loaded.kind === targetKind;
  useEffect(() => {
    if (!ready || !authenticated || !targetId || !targetKind || isPresent || matches) return;
    const controller = new AbortController();
    let current = true;
    void (async () => {
      try {
        let record: Note | Todo;
        if (targetKind === 'todo') record = (await api.getTodo(targetId, controller.signal)).todo;
        else record = (await api.getInput(targetId, controller.signal)).note;
        if (current)
          setLoaded({
            api,
            snapshot,
            userId,
            sessionVersion,
            id: targetId,
            kind: targetKind,
            record,
            error: false,
          });
      } catch {
        if (current)
          setLoaded({
            api,
            snapshot,
            userId,
            sessionVersion,
            id: targetId,
            kind: targetKind,
            record: null,
            error: true,
          });
      }
    })();
    return () => {
      current = false;
      controller.abort();
    };
  }, [
    api,
    snapshot,
    userId,
    sessionVersion,
    authenticated,
    targetId,
    targetKind,
    ready,
    isPresent,
    matches,
  ]);
  let record = present ?? null;
  if (!record && matches) record = loaded.record;
  return { record, error: matches && loaded.error };
}

export function InputsPage() {
  const api = useApi();
  const { snapshot, loading, error, refresh, openComposer } = useGlobalInputs();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const requestedTab = params.get('tab');
  const selectedTab: Tab = isTab(requestedTab) ? requestedTab : 'todos';
  const viewer = useMe();
  const { status: authStatus, sessionVersion } = useAuth();
  const target = useMemo(() => linkedInputTarget(location.hash), [location.hash]);
  const linked = useLinkedInput(
    api,
    viewer?.sub ?? null,
    sessionVersion,
    authStatus === 'authenticated',
    target,
    snapshot,
  );
  const todos = useMemo(() => {
    const rows = snapshot?.todos ?? [];
    const record = linked.record;
    if (record?.kind !== 'todo' || rows.some((row) => row.todo_id === record.todo_id)) return rows;
    return [...rows, record];
  }, [snapshot, linked.record]);
  const notes = useMemo(() => {
    const rows = snapshot?.notes ?? [];
    const record = linked.record;
    if (!record || record.kind === 'todo' || rows.some((row) => row.note_id === record.note_id))
      return rows;
    return [...rows, record];
  }, [snapshot, linked.record]);
  let tab = selectedTab;
  if (linked.record) {
    if (linked.record.kind === 'todo') {
      if (
        linked.record.archived_at ||
        linked.record.status === 'done' ||
        linked.record.status === 'canceled'
      )
        tab = 'archive';
      else tab = 'todos';
    } else if (linked.record.status === 'archived') tab = 'archive';
    else tab = linked.record.kind;
  }
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState<TodoStatus | 'all'>('all');
  const [priority, setPriority] = useState<TodoPriority | 'all'>('all');
  const [workingId, setWorkingId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [showDropChannelForm, setShowDropChannelForm] = useState(false);

  const activeTodos = useMemo(
    () =>
      todos.filter(
        (todo) => todo.archived_at === null && todo.status !== 'done' && todo.status !== 'canceled',
      ),
    [todos],
  );
  const archivedTodos = useMemo(
    () =>
      todos.filter(
        (todo) => todo.archived_at !== null || todo.status === 'done' || todo.status === 'canceled',
      ),
    [todos],
  );
  const guidance = useMemo(() => notes.filter((note) => note.kind === 'guidance'), [notes]);
  const research = useMemo(() => notes.filter((note) => note.kind === 'research'), [notes]);
  useResearchPolling(research, refresh);
  const pendingProposals = useMemo(
    () => (snapshot?.proposals ?? []).filter((proposal) => proposal.status === 'pending'),
    [snapshot],
  );
  const rejectedProposals = useMemo(
    () => (snapshot?.proposals ?? []).filter((proposal) => proposal.status === 'rejected'),
    [snapshot],
  );
  const archivedNotes = useMemo(() => notes.filter((note) => note.status === 'archived'), [notes]);
  const dropChannels = snapshot?.drop_channels ?? [];

  // Search and Ask citations target an authorized row on the selected tab.
  useEffect(() => {
    if (!snapshot || !location.hash) return;
    let id: string;
    try {
      id = decodeURIComponent(location.hash.slice(1));
    } catch {
      return;
    }
    document.getElementById(id)?.scrollIntoView?.({ block: 'center' });
  }, [snapshot, tab, location.hash, linked.record]);

  const filteredTodos = activeTodos.filter((todo) => {
    if (target?.kind === 'todo' && target.id === todo.todo_id) return true;
    const term = query.trim().toLocaleLowerCase('en-US');
    if (
      term &&
      !`${todo.title} ${todo.body_md} ${todo.tags.join(' ')}`
        .toLocaleLowerCase('en-US')
        .includes(term)
    ) {
      return false;
    }
    if (status !== 'all' && todo.status !== status) return false;
    if (priority !== 'all' && todo.priority !== priority) return false;
    return true;
  });
  const filterNote = (note: Note) => {
    if (target?.kind === 'note' && target.id === note.note_id) return true;
    const term = query.trim().toLocaleLowerCase('en-US');
    return !term || `${note.title ?? ''} ${note.text}`.toLocaleLowerCase('en-US').includes(term);
  };
  const filteredPendingProposals = pendingProposals.filter((proposal) =>
    `${proposal.title} ${proposal.body_md} ${proposal.rationale_md ?? ''}`
      .toLocaleLowerCase('en-US')
      .includes(query.trim().toLocaleLowerCase('en-US')),
  );

  async function mutate(id: string, action: () => Promise<unknown>) {
    setWorkingId(id);
    setActionError(null);
    try {
      await action();
      await refresh();
    } catch (cause) {
      setActionError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setWorkingId(null);
    }
  }

  function selectTab(next: Tab) {
    const updated = new URLSearchParams(params);
    updated.set('tab', next);
    void navigate(
      { pathname: location.pathname, search: `?${updated}`, hash: '' },
      { replace: true },
    );
  }

  async function moveTodo(todo: Todo, direction: -1 | 1) {
    const currentIndex = activeTodos.findIndex((candidate) => candidate.todo_id === todo.todo_id);
    const nextIndex = currentIndex + direction;
    if (currentIndex < 0 || nextIndex < 0 || nextIndex >= activeTodos.length) return;
    const order = activeTodos.map((candidate) => candidate.todo_id);
    [order[currentIndex], order[nextIndex]] = [order[nextIndex]!, order[currentIndex]!];
    await mutate(todo.todo_id, () => api.reorderTodos(order));
  }

  const tabCounts: Record<Tab, number> = {
    todos: activeTodos.length,
    research: research.filter((note) => note.status !== 'archived').length,
    guidance: guidance.filter((note) => note.status !== 'archived').length,
    'drop-channels': dropChannels.filter((channel) => channel.enabled).length,
    proposals: pendingProposals.length,
    archive: archivedTodos.length + archivedNotes.length + rejectedProposals.length,
  };
  let newInputKind: 'todo' | 'guidance' | 'research' = 'todo';
  let newInputLabel = 'to-do';
  if (tab === 'guidance') {
    newInputKind = 'guidance';
    newInputLabel = 'guidance';
  } else if (tab === 'research') {
    newInputKind = 'research';
    newInputLabel = 'research request';
  }

  async function createDropChannel(input: CreateDropChannel) {
    setWorkingId(input.channel_id);
    setActionError(null);
    try {
      await api.createDropChannel(input);
      await refresh();
      setShowDropChannelForm(false);
    } catch (cause) {
      setActionError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setWorkingId(null);
    }
  }

  return (
    <main className="mx-auto max-w-5xl px-4 py-8 sm:px-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-semibold tracking-tight text-foreground">
            Inputs
          </h1>
          <p className="max-w-[65ch] text-base text-muted-foreground sm:text-sm">
            Global to-dos, standing guidance, and queued research outside any one digest.
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            if (tab === 'drop-channels') {
              setShowDropChannelForm(true);
            } else {
              openComposer(newInputKind);
            }
          }}
          className="rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground ring-1 ring-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
        >
          {tab === 'drop-channels' ? 'Add channel' : `New ${newInputLabel}`}
        </button>
      </div>

      <nav
        aria-label="Input types"
        className="flex gap-1 overflow-x-auto border-b border-foreground/10 pt-6"
      >
        {TABS.map((candidate) => (
          <button
            key={candidate}
            type="button"
            aria-current={tab === candidate ? 'page' : undefined}
            onClick={() => selectTab(candidate)}
            className={
              tab === candidate
                ? 'shrink-0 border-b-2 border-primary px-3 py-2 text-sm text-foreground'
                : 'shrink-0 border-b-2 border-transparent px-3 py-2 text-sm text-muted-foreground hover:text-foreground'
            }
          >
            {candidate === 'drop-channels'
              ? 'Drop channels'
              : candidate[0]!.toUpperCase() + candidate.slice(1)}{' '}
            <span className="tabular-nums">({tabCounts[candidate]})</span>
          </button>
        ))}
      </nav>

      <div className="@container py-4">
        <div className="flex flex-col gap-3 @md:flex-row @md:items-center">
          <input
            type="search"
            name="input-search"
            aria-label="Search inputs"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={`Search ${tab}…`}
            className="min-w-0 flex-1 rounded-lg bg-card px-3 py-2.5 text-base text-foreground ring-1 ring-foreground/10 outline-none placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-ring sm:py-2 sm:text-sm"
          />
          {tab === 'todos' ? (
            <>
              <FilterSelect
                label="Status"
                value={status}
                onChange={(value) => setStatus(value as TodoStatus | 'all')}
                options={['all', 'open', 'in_progress', 'blocked']}
              />
              <FilterSelect
                label="Priority"
                value={priority}
                onChange={(value) => setPriority(value as TodoPriority | 'all')}
                options={['all', 'urgent', 'high', 'normal', 'low']}
              />
            </>
          ) : null}
        </div>
      </div>

      {linked.error ? (
        <p role="alert" className="text-danger">
          The linked input could not be loaded.
        </p>
      ) : null}
      {error || actionError ? (
        <p className="rounded-lg bg-danger/10 p-3 text-base text-danger sm:text-sm" role="alert">
          {actionError ?? error}
        </p>
      ) : null}
      {loading ? (
        <div className="space-y-3 py-4" aria-hidden="true">
          {[0, 1, 2].map((item) => (
            <div key={item} className="h-28 animate-pulse rounded-xl bg-muted" />
          ))}
        </div>
      ) : null}

      {snapshot && tab === 'todos' ? (
        <section aria-label="Canonical to-dos">
          {filteredTodos.length === 0 ? (
            <EmptyState
              title="No matching to-dos."
              detail="Clear the filters or create a global action item."
            />
          ) : (
            <ul className="divide-y divide-foreground/10">
              {filteredTodos.map((todo) => {
                const activeIndex = activeTodos.findIndex(
                  (candidate) => candidate.todo_id === todo.todo_id,
                );
                return (
                  <TodoRow
                    key={todo.todo_id}
                    todo={todo}
                    first={activeIndex === 0}
                    last={activeIndex === activeTodos.length - 1}
                    working={workingId === todo.todo_id}
                    move={(direction) => void moveTodo(todo, direction)}
                    edit={() => openComposer('todo', todo)}
                    setStatus={(next) =>
                      void mutate(todo.todo_id, () =>
                        api.updateTodo(todo.todo_id, { status: next }),
                      )
                    }
                    archive={() => void mutate(todo.todo_id, () => api.archiveTodo(todo.todo_id))}
                  />
                );
              })}
            </ul>
          )}
        </section>
      ) : null}

      {snapshot && tab === 'guidance' ? (
        <NoteList
          notes={guidance.filter((note) => note.status !== 'archived' && filterNote(note))}
          empty="No guidance matches."
          edit={(note) => openComposer('guidance', note)}
          archive={(note) => void mutate(note.note_id, () => api.archiveInput(note.note_id))}
          workingId={workingId}
        />
      ) : null}

      {snapshot && tab === 'research' ? (
        <NoteList
          notes={research.filter((note) => note.status !== 'archived' && filterNote(note))}
          empty="No research requests match."
          edit={(note) => openComposer('research', note)}
          archive={(note) => void mutate(note.note_id, () => api.archiveInput(note.note_id))}
          workingId={workingId}
        />
      ) : null}

      {snapshot && tab === 'drop-channels' ? (
        <DropChannelsPanel
          channels={dropChannels.filter((channel) => {
            const term = query.trim().toLocaleLowerCase('en-US');
            return (
              !term ||
              `${channel.label} ${channel.channel_id}`.toLocaleLowerCase('en-US').includes(term)
            );
          })}
          showForm={showDropChannelForm}
          workingId={workingId}
          cancelCreate={() => setShowDropChannelForm(false)}
          create={(input) => void createDropChannel(input)}
          toggle={(channel) =>
            void mutate(channel.channel_id, () =>
              api.updateDropChannel(channel.channel_id, { enabled: !channel.enabled }),
            )
          }
          remove={(channel) => {
            const confirmed = window.confirm(
              `Remove ${channel.label}? This deletes its intake cursor and history. Pause it instead if you may use it again.`,
            );
            if (!confirmed) return;
            void mutate(channel.channel_id, () => api.deleteDropChannel(channel.channel_id));
          }}
        />
      ) : null}

      {snapshot && tab === 'proposals' ? (
        <section className="space-y-3">
          <div className="rounded-lg bg-accent/10 p-3">
            <p className="text-base text-foreground sm:text-sm">
              Agent proposals stay separate from your canonical list until you accept them.
            </p>
          </div>
          {filteredPendingProposals.length === 0 ? (
            <EmptyState
              title="No pending proposals."
              detail="Scheduled sweeps may suggest items here."
            />
          ) : (
            filteredPendingProposals.map((proposal) => (
              <ProposalRow
                key={proposal.proposal_id}
                proposal={proposal}
                working={workingId === proposal.proposal_id}
                accept={(edits) =>
                  void mutate(proposal.proposal_id, () =>
                    api.acceptTodoProposal(proposal.proposal_id, edits),
                  )
                }
                reject={() =>
                  void mutate(proposal.proposal_id, () =>
                    api.rejectTodoProposal(proposal.proposal_id),
                  )
                }
              />
            ))
          )}
        </section>
      ) : null}

      {snapshot && tab === 'archive' ? (
        <section className="space-y-6">
          <ArchiveGroup title="Completed or archived to-dos" empty={archivedTodos.length === 0}>
            {archivedTodos.map((todo) => (
              <ArchiveRow
                key={todo.todo_id}
                id={`todo-${todo.todo_id}`}
                title={todo.title}
                detail={`${todo.status}${todo.archived_at ? ' · archived' : ''}`}
                restore={() =>
                  void mutate(todo.todo_id, () =>
                    todo.archived_at
                      ? api.restoreTodo(todo.todo_id)
                      : api.updateTodo(todo.todo_id, { status: 'open' }),
                  )
                }
              />
            ))}
          </ArchiveGroup>
          <ArchiveGroup title="Archived guidance and research" empty={archivedNotes.length === 0}>
            {archivedNotes.map((note) => (
              <ArchiveRow
                key={note.note_id}
                id={`input-${note.note_id}`}
                title={note.title ?? 'Untitled'}
                detail={note.kind}
                restore={() => void mutate(note.note_id, () => api.restoreInput(note.note_id))}
              />
            ))}
          </ArchiveGroup>
          <ArchiveGroup title="Dismissed proposals" empty={rejectedProposals.length === 0}>
            {rejectedProposals.map((proposal) => (
              <ArchiveRow
                key={proposal.proposal_id}
                title={proposal.title}
                detail="Rejected proposal"
                restore={() =>
                  void mutate(proposal.proposal_id, () =>
                    api.restoreTodoProposal(proposal.proposal_id),
                  )
                }
              />
            ))}
          </ArchiveGroup>
        </section>
      ) : null}
    </main>
  );
}

/** Configure Slack channels that feed the per-operator research intake pass. */
function DropChannelsPanel({
  channels,
  showForm,
  workingId,
  cancelCreate,
  create,
  toggle,
  remove,
}: {
  channels: DropChannel[];
  showForm: boolean;
  workingId: string | null;
  cancelCreate: () => void;
  create: (input: CreateDropChannel) => void;
  toggle: (channel: DropChannel) => void;
  remove: (channel: DropChannel) => void;
}) {
  const [channelId, setChannelId] = useState('');
  const [label, setLabel] = useState('');
  const [authorFilter, setAuthorFilter] = useState<'operator' | 'anyone'>('operator');
  const [ackEmoji, setAckEmoji] = useState('eyes');
  const [doneEmoji, setDoneEmoji] = useState('white_check_mark');

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    create({
      channel_id: channelId.trim(),
      label: label.trim(),
      author_filter: authorFilter,
      ack_emoji: ackEmoji.trim(),
      done_emoji: doneEmoji.trim(),
      enabled: true,
    });
  }

  return (
    <section className="space-y-4" aria-label="Slack research drop channels">
      <p className="rounded-lg bg-accent/10 p-3 text-base text-foreground sm:text-sm">
        Research sweeps ingest matching Slack messages after the saved cursor. Reactions are human
        receipts; the server-held cursor and note idempotency key prevent duplicate work.
      </p>

      {showForm ? (
        <form
          onSubmit={submit}
          className="space-y-4 rounded-xl bg-card p-4 ring-1 ring-foreground/10"
        >
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="space-y-1 text-sm text-muted-foreground">
              Channel ID
              <input
                required
                name="drop-channel-id"
                value={channelId}
                onChange={(event) => setChannelId(event.target.value)}
                placeholder="C0123456789"
                className="w-full rounded-lg bg-background px-3 py-2.5 text-base text-foreground ring-1 ring-foreground/10 sm:py-2 sm:text-sm"
              />
            </label>
            <label className="space-y-1 text-sm text-muted-foreground">
              Label
              <input
                required
                name="drop-channel-label"
                value={label}
                onChange={(event) => setLabel(event.target.value)}
                placeholder="#research-drops"
                className="w-full rounded-lg bg-background px-3 py-2.5 text-base text-foreground ring-1 ring-foreground/10 sm:py-2 sm:text-sm"
              />
            </label>
            <label className="space-y-1 text-sm text-muted-foreground">
              Messages to ingest
              <select
                name="drop-channel-author-filter"
                value={authorFilter}
                onChange={(event) =>
                  setAuthorFilter(event.target.value === 'anyone' ? 'anyone' : 'operator')
                }
                className="w-full rounded-lg bg-background px-3 py-2.5 text-base text-foreground ring-1 ring-foreground/10 sm:py-2 sm:text-sm"
              >
                <option value="operator">Only mine</option>
                <option value="anyone">Anyone</option>
              </select>
            </label>
            <label className="space-y-1 text-sm text-muted-foreground">
              Acknowledgement emoji
              <input
                required
                name="drop-channel-ack-emoji"
                value={ackEmoji}
                onChange={(event) => setAckEmoji(event.target.value)}
                className="w-full rounded-lg bg-background px-3 py-2.5 text-base text-foreground ring-1 ring-foreground/10 sm:py-2 sm:text-sm"
              />
            </label>
            <label className="space-y-1 text-sm text-muted-foreground">
              Published emoji
              <input
                required
                name="drop-channel-done-emoji"
                value={doneEmoji}
                onChange={(event) => setDoneEmoji(event.target.value)}
                className="w-full rounded-lg bg-background px-3 py-2.5 text-base text-foreground ring-1 ring-foreground/10 sm:py-2 sm:text-sm"
              />
            </label>
          </div>
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={workingId === channelId.trim()}
              className="rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50"
            >
              Add channel
            </button>
            <button
              type="button"
              onClick={cancelCreate}
              className="rounded-md px-3 py-2 text-sm text-muted-foreground hover:bg-muted"
            >
              Cancel
            </button>
          </div>
        </form>
      ) : null}

      {channels.length === 0 ? (
        <EmptyState
          title="No drop channels configured."
          detail="Add a Slack channel to make research intake explicit and observable."
        />
      ) : (
        <ul className="divide-y divide-foreground/10">
          {channels.map((channel) => (
            <li key={channel.channel_id} className="flex flex-wrap items-start gap-3 py-4">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-baseline gap-2">
                  <h2 className="text-lg font-semibold text-foreground sm:text-base">
                    {channel.label}
                  </h2>
                  <span className="rounded-full bg-muted px-2 py-1 text-sm text-muted-foreground">
                    {channel.enabled ? 'enabled' : 'paused'}
                  </span>
                </div>
                <p className="pt-1 font-mono text-sm text-muted-foreground">{channel.channel_id}</p>
                <p className="pt-1 text-base text-muted-foreground sm:text-sm">
                  {channel.author_filter === 'operator' ? 'Operator messages' : 'All messages'} · :
                  {channel.ack_emoji}: receipt · :{channel.done_emoji}: published
                </p>
                <p className="pt-1 text-base text-muted-foreground sm:text-sm">
                  {channel.last_intake_at
                    ? `Last intake ${new Date(channel.last_intake_at).toLocaleString()} · cursor ${channel.last_intake_ts ?? 'none'}`
                    : 'Intake has not run yet.'}
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={workingId === channel.channel_id}
                  onClick={() => toggle(channel)}
                  className="rounded-md px-2 py-1.5 text-sm text-primary hover:bg-primary/10 disabled:opacity-50"
                >
                  {channel.enabled ? 'Pause' : 'Enable'}
                </button>
                <button
                  type="button"
                  disabled={workingId === channel.channel_id}
                  onClick={() => remove(channel)}
                  className="rounded-md px-2 py-1.5 text-sm text-danger hover:bg-danger/10 disabled:opacity-50"
                >
                  Remove
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function FilterSelect({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: string[];
}) {
  return (
    <label className="flex shrink-0 items-center gap-2 text-base text-muted-foreground sm:text-sm">
      {label}
      <select
        name={label.toLocaleLowerCase('en-US')}
        aria-label={`Filter by ${label.toLocaleLowerCase('en-US')}`}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="rounded-lg bg-card px-3 py-2.5 text-base text-foreground ring-1 ring-foreground/10 outline-none focus-visible:ring-2 focus-visible:ring-ring sm:py-2 sm:text-sm"
      >
        {options.map((option) => (
          <option key={option} value={option}>
            {option.replaceAll('_', ' ')}
          </option>
        ))}
      </select>
    </label>
  );
}

function TodoRow({
  todo,
  first,
  last,
  working,
  move,
  edit,
  setStatus,
  archive,
}: {
  todo: Todo;
  first: boolean;
  last: boolean;
  working: boolean;
  move: (direction: -1 | 1) => void;
  edit: () => void;
  setStatus: (status: TodoStatus) => void;
  archive: () => void;
}) {
  const api = useApi();
  const [events, setEvents] = useState<TodoEvent[] | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);

  async function toggleHistory() {
    const next = !historyOpen;
    setHistoryOpen(next);
    if (next && events === null) {
      setEvents((await api.listTodoEvents(todo.todo_id)).events);
    }
  }

  return (
    <li id={`todo-${todo.todo_id}`} className="scroll-mt-36 py-4">
      <div className="flex items-start gap-3">
        <select
          name={`status-${todo.todo_id}`}
          aria-label={`Status for ${todo.title}`}
          value={todo.status}
          disabled={working}
          onChange={(event) => setStatus(event.target.value as TodoStatus)}
          className="mt-0.5 shrink-0 rounded-md bg-card px-2 py-2 text-base ring-1 ring-foreground/10 sm:py-1.5 sm:text-sm"
        >
          {['open', 'in_progress', 'blocked', 'done', 'canceled'].map((candidate) => (
            <option key={candidate} value={candidate}>
              {candidate.replaceAll('_', ' ')}
            </option>
          ))}
        </select>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-2">
            <h2 className="text-lg font-semibold text-foreground sm:text-base">{todo.title}</h2>
            <span className="rounded-full bg-muted px-2 py-1 text-sm text-muted-foreground">
              {todo.priority}
            </span>
            {todo.tags.map((tag) => (
              <span key={tag} className="rounded-full bg-primary/10 px-2 py-1 text-sm text-primary">
                {tag}
              </span>
            ))}
          </div>
          {todo.body_md ? (
            <div className="line-clamp-3 pt-1 text-base text-muted-foreground sm:text-sm">
              <MarkdownPrimitive body={todo.body_md} />
            </div>
          ) : null}
          <p className="pt-1 text-base text-muted-foreground sm:text-sm">
            {todo.due_at ? `Due ${new Date(todo.due_at).toLocaleString()}` : 'No due date'}
            {todo.remind_at ? ` · reminder ${new Date(todo.remind_at).toLocaleString()}` : ''}
            {todo.snoozed_until
              ? ` · snoozed until ${new Date(todo.snoozed_until).toLocaleString()}`
              : ''}
          </p>
          <div className="flex flex-wrap gap-1 pt-2">
            <button
              type="button"
              disabled={first || working}
              onClick={() => move(-1)}
              className="rounded-md px-2 py-1.5 text-sm text-muted-foreground hover:bg-muted disabled:opacity-30"
            >
              Move up
            </button>
            <button
              type="button"
              disabled={last || working}
              onClick={() => move(1)}
              className="rounded-md px-2 py-1.5 text-sm text-muted-foreground hover:bg-muted disabled:opacity-30"
            >
              Move down
            </button>
            <button
              type="button"
              onClick={edit}
              className="rounded-md px-2 py-1.5 text-sm text-primary hover:bg-primary/10"
            >
              Edit
            </button>
            <button
              type="button"
              onClick={() => void toggleHistory()}
              className="rounded-md px-2 py-1.5 text-sm text-muted-foreground hover:bg-muted"
            >
              {historyOpen ? 'Hide history' : 'History'}
            </button>
            <button
              type="button"
              disabled={working}
              onClick={archive}
              className="rounded-md px-2 py-1.5 text-sm text-muted-foreground hover:bg-muted"
            >
              Archive
            </button>
          </div>
          {historyOpen ? <TodoHistory events={events} /> : null}
        </div>
      </div>
    </li>
  );
}

function TodoHistory({ events }: { events: TodoEvent[] | null }) {
  if (!events)
    return <p className="pt-2 text-base text-muted-foreground sm:text-sm">Loading history…</p>;
  return (
    <ol className="space-y-1 border-l border-foreground/10 pt-2 pl-3">
      {events.map((event) => (
        <li key={event.event_id} className="text-base text-muted-foreground sm:text-sm">
          {event.action.replaceAll('_', ' ')} · {new Date(event.created_at).toLocaleString()}
        </li>
      ))}
    </ol>
  );
}

function NoteList({
  notes,
  empty,
  edit,
  archive,
  workingId,
}: {
  notes: Note[];
  empty: string;
  edit: (note: Note) => void;
  archive: (note: Note) => void;
  workingId: string | null;
}) {
  if (notes.length === 0)
    return <EmptyState title={empty} detail="Create a new input to get started." />;
  return (
    <ul className="divide-y divide-foreground/10">
      {notes.map((note) => (
        <li key={note.note_id} id={`input-${note.note_id}`} className="scroll-mt-36 space-y-2 py-4">
          <div className="flex flex-wrap items-baseline gap-2">
            <h2 className="text-lg font-semibold text-foreground sm:text-base">
              {note.title ?? 'Untitled'}
            </h2>
            {note.kind === 'research' ? (
              <ResearchStatePill note={note} />
            ) : (
              <span className="rounded-full bg-muted px-2 py-1 text-sm text-muted-foreground">
                {note.status}
              </span>
            )}
            {note.kind === 'guidance'
              ? note.scopes.map((scope) => (
                  <span
                    key={scope}
                    className="rounded-full bg-primary/10 px-2 py-1 text-sm text-primary"
                  >
                    {scope.replaceAll('_', ' ')}
                  </span>
                ))
              : null}
          </div>
          <div className="line-clamp-4 text-base text-muted-foreground sm:text-sm">
            <MarkdownPrimitive body={note.text} />
          </div>
          {note.kind === 'research' && note.links.length > 0 ? (
            <ul className="flex flex-wrap gap-2">
              {note.links.map((link) => (
                <li key={link.url}>
                  <a
                    href={link.url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-sm text-primary underline"
                  >
                    {link.label ?? new URL(link.url).hostname}
                  </a>
                </li>
              ))}
            </ul>
          ) : null}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => edit(note)}
              className="rounded-md px-2 py-1.5 text-sm text-primary hover:bg-primary/10"
            >
              Edit
            </button>
            <button
              type="button"
              disabled={workingId === note.note_id}
              onClick={() => archive(note)}
              className="rounded-md px-2 py-1.5 text-sm text-muted-foreground hover:bg-muted"
            >
              Archive
            </button>
            {note.research?.artifact_href ? (
              <Link
                to={note.research.artifact_href}
                className="rounded-md px-2 py-1.5 text-sm text-primary"
              >
                View result
              </Link>
            ) : null}
          </div>
        </li>
      ))}
    </ul>
  );
}

function ProposalRow({
  proposal,
  working,
  accept,
  reject,
}: {
  proposal: TodoProposal;
  working: boolean;
  accept: (edits: {
    title?: string;
    body_md?: string;
    priority?: TodoPriority;
    due_at?: string | null;
  }) => void;
  reject: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(proposal.title);
  const [body, setBody] = useState(proposal.body_md);
  const [priority, setPriority] = useState(proposal.priority);
  return (
    <article className="rounded-xl bg-card p-4 ring-1 ring-foreground/10">
      {editing ? (
        <div className="space-y-3">
          <input
            name={`proposal-title-${proposal.proposal_id}`}
            aria-label="Proposal title"
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            className="w-full rounded-lg bg-background px-3 py-2.5 text-base ring-1 ring-foreground/10 sm:py-2 sm:text-sm"
          />
          <textarea
            name={`proposal-body-${proposal.proposal_id}`}
            aria-label="Proposal notes"
            rows={4}
            value={body}
            onChange={(event) => setBody(event.target.value)}
            className="w-full rounded-lg bg-background px-3 py-2.5 text-base ring-1 ring-foreground/10 sm:py-2 sm:text-sm"
          />
          <FilterSelect
            label="Priority"
            value={priority}
            onChange={(value) => setPriority(value as TodoPriority)}
            options={['urgent', 'high', 'normal', 'low']}
          />
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-baseline gap-2">
            <h2 className="text-lg font-semibold text-foreground sm:text-base">{proposal.title}</h2>
            <span className="rounded-full bg-accent/20 px-2 py-1 text-sm text-accent-foreground">
              AI proposed
            </span>
          </div>
          {proposal.rationale_md ? (
            <p className="pt-1 text-base text-muted-foreground sm:text-sm">
              {proposal.rationale_md}
            </p>
          ) : null}
          <div className="pt-2 text-base text-muted-foreground sm:text-sm">
            <MarkdownPrimitive body={proposal.body_md} />
          </div>
        </>
      )}
      <div className="flex flex-wrap gap-2 pt-3">
        <button
          type="button"
          disabled={working}
          onClick={() => accept(editing ? { title: title.trim(), body_md: body, priority } : {})}
          className="rounded-md bg-primary px-3 py-1.5 text-sm text-primary-foreground"
        >
          {editing ? 'Accept edited' : 'Accept'}
        </button>
        <button
          type="button"
          onClick={() => setEditing((current) => !current)}
          className="rounded-md bg-muted px-3 py-1.5 text-sm text-foreground"
        >
          {editing ? 'Cancel edits' : 'Edit and accept'}
        </button>
        <button
          type="button"
          disabled={working}
          onClick={reject}
          className="rounded-md px-3 py-1.5 text-sm text-muted-foreground hover:bg-muted"
        >
          Dismiss
        </button>
      </div>
    </article>
  );
}

function EmptyState({ title, detail }: { title: string; detail: string }) {
  return (
    <div className="space-y-1 py-12 text-center">
      <p className="text-base font-medium text-foreground">{title}</p>
      <p className="text-base text-muted-foreground sm:text-sm">{detail}</p>
    </div>
  );
}

function ArchiveGroup({
  title,
  empty,
  children,
}: {
  title: string;
  empty: boolean;
  children: React.ReactNode;
}) {
  return (
    <section>
      <h2 className="text-lg font-semibold text-foreground">{title}</h2>
      {empty ? (
        <p className="pt-2 text-base text-muted-foreground sm:text-sm">Nothing here.</p>
      ) : (
        <ul className="divide-y divide-foreground/10 pt-2">{children}</ul>
      )}
    </section>
  );
}

function ArchiveRow({
  id,
  title,
  detail,
  restore,
}: {
  id?: string;
  title: string;
  detail: string;
  restore: () => void;
}) {
  return (
    <li id={id} className="scroll-mt-36 flex items-center justify-between gap-4 py-3">
      <div className="min-w-0">
        <p className="truncate text-base font-medium text-foreground sm:text-sm">{title}</p>
        <p className="text-base text-muted-foreground sm:text-sm">{detail}</p>
      </div>
      <button
        type="button"
        onClick={restore}
        className="shrink-0 rounded-md bg-muted px-3 py-1.5 text-sm text-foreground"
      >
        Restore
      </button>
    </li>
  );
}
